import React, { useEffect, useMemo, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import './calculators.css';
import './EmiInputBands.css';
import './InterestRateImpactCalculator.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 6;
const maxInterestRate = 50;
const interestRateStep = 0.25;

const minTenureYears = 1;
const maxTenureYears = 30;

const minLoanAmount = 0;
const maxLoanAmount = 1000000000;

/** Matches the ₹1 crore · 8% · 20y example in the rate-movements article. */
const STANDALONE_DEFAULTS = {
  interestRate: 8,
  tenureYears: 20,
  loanAmount: 10000000,
  rateChangePct: 1,
};

const RATE_CHANGE_OPTIONS = [
  { value: -2, label: '−2%' },
  { value: -1, label: '−1%' },
  { value: -0.5, label: '−0.5%' },
  { value: 0.5, label: '+0.5%' },
  { value: 1, label: '+1%' },
  { value: 2, label: '+2%' },
];

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(
    maxInterestRate,
    Math.max(minInterestRate, Number(snapped.toFixed(2))),
  );
}

function formatSignedPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
}

function formatTenureYearsPrecise(years) {
  const n = Number(years);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 100) return '100+ years';
  const rounded = Math.round(n * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 0.05) {
    return formatYearsCount(Math.round(rounded));
  }
  return `${rounded.toFixed(1)} years`;
}

/**
 * Years to repay P at fixed EMI and annual rate.
 * Returns { feasible, years } — not feasible when EMI ≤ interest-only.
 */
export function tenureYearsFromEmi(loanAmount, monthlyEmi, annualInterestRatePct) {
  const P = Number(loanAmount);
  const emi = Number(monthlyEmi);
  if (!(P > 0) || !(emi > 0)) return { feasible: false, years: null };

  const annualRate = Number(annualInterestRatePct);
  if (!Number.isFinite(annualRate) || annualRate <= 0) {
    return { feasible: true, years: P / emi / 12 };
  }

  const r = annualRate / 12 / 100;
  if (emi <= P * r * (1 + 1e-10)) {
    return { feasible: false, years: null };
  }

  const n = Math.log(emi / (emi - P * r)) / Math.log(1 + r);
  if (!Number.isFinite(n) || n <= 0) return { feasible: false, years: null };
  return { feasible: true, years: n / 12 };
}

export function computeRateChangeImpact({
  interestRate,
  loanAmount,
  tenureYears,
  rateChangePct,
}) {
  const baseRate = Number(interestRate);
  const delta = Number(rateChangePct);
  const newRate = baseRate + delta;
  const baseEmi = computeEmi(loanAmount, baseRate, tenureYears);
  const emiIfTenureFixed = computeEmi(loanAmount, newRate, tenureYears);
  const tenureIfEmiFixed = tenureYearsFromEmi(loanAmount, baseEmi, newRate);

  return {
    baseRate,
    newRate,
    rateChangePct: delta,
    baseEmi,
    emiIfTenureFixed,
    emiDelta: emiIfTenureFixed - baseEmi,
    tenureIfEmiFixedYears: tenureIfEmiFixed.years,
    tenureFeasible: tenureIfEmiFixed.feasible,
    tenureDeltaYears: tenureIfEmiFixed.feasible
      ? tenureIfEmiFixed.years - Number(tenureYears)
      : null,
  };
}

export default function InterestRateImpactCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const [interestRate, setInterestRate] = useState(STANDALONE_DEFAULTS.interestRate);
  const [tenureYears, setTenureYears] = useState(STANDALONE_DEFAULTS.tenureYears);
  const [loanAmount, setLoanAmount] = useState(STANDALONE_DEFAULTS.loanAmount);
  const [loanAmountForWords, setLoanAmountForWords] = useState(
    STANDALONE_DEFAULTS.loanAmount,
  );
  const [rateChangePct, setRateChangePct] = useState(
    STANDALONE_DEFAULTS.rateChangePct,
  );

  const impact = useMemo(
    () =>
      computeRateChangeImpact({
        interestRate,
        loanAmount,
        tenureYears,
        rateChangePct,
      }),
    [interestRate, loanAmount, tenureYears, rateChangePct],
  );

  const loanAmountWords =
    loanAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(loanAmountForWords))
      : '';

  function handleStartOver(e) {
    e.preventDefault();
    setInterestRate(STANDALONE_DEFAULTS.interestRate);
    setTenureYears(STANDALONE_DEFAULTS.tenureYears);
    setLoanAmount(STANDALONE_DEFAULTS.loanAmount);
    setLoanAmountForWords(STANDALONE_DEFAULTS.loanAmount);
    setRateChangePct(STANDALONE_DEFAULTS.rateChangePct);
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator interest-rate-impact${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Interest Rate Impact Calculator</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          See how a change in your floating home-loan rate affects EMI (if tenure stays the
          same) or tenure (if you keep paying the same EMI).
        </p>
      </div>

      <ImpactResultPanel impact={impact} tenureYears={tenureYears} />

      <div className="border-top-light nps-ups-divider" />

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="iri-interest-rate"
          label="Annual interest rate"
          tooltip="Your current floating home-loan rate (before the change you want to stress-test)."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.interestRate}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="iri-loan-amount"
            label="Loan amount"
            tooltip="Outstanding principal (or sanctioned amount) used for this illustration."
            value={Math.min(loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(maxLoanAmount, Math.max(0, v));
              setLoanAmount(next);
              setLoanAmountForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setLoanAmountForWords(Math.min(maxLoanAmount, Math.max(0, n)));
              } else {
                setLoanAmountForWords(0);
              }
            }}
            min={minLoanAmount}
            max={maxLoanAmount}
            step={100000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.loanAmount}
          />
          {loanAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {loanAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="iri-tenure-years"
          label="Tenure"
          tooltip="Current contracted repayment period in whole years."
          value={tenureYears}
          setValue={setTenureYears}
          min={minTenureYears}
          max={maxTenureYears}
          step={1}
          format={formatYearsCount}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.tenureYears}
        />
      </section>

      <div
        className="iri-rate-change"
        role="group"
        aria-label="Interest rate change to stress-test"
      >
        <span className="iri-rate-change__label">Interest rate change</span>
        <div className="iri-rate-change__chips">
          {RATE_CHANGE_OPTIONS.map((opt) => {
            const selected = Number(rateChangePct) === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`iri-rate-change__chip${
                  selected ? ' iri-rate-change__chip--selected' : ''
                }`}
                aria-pressed={selected}
                onClick={() => setRateChangePct(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative amortisation only. Assumes a sudden rate change on the full outstanding
        principal with no fees, prepayments, or lender caps. Not advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="interest-rate-impact" />
      </div>
    </div>
  );
}

function ImpactResultPanel({ impact, tenureYears }) {
  const { formatInr } = useCalcAmountDisplayMode();
  const baseEmiDisplay = formatInr(impact.baseEmi || 0);
  const newEmiDisplay = formatInr(impact.emiIfTenureFixed || 0);
  const emiDeltaDisplay = formatInr(Math.abs(impact.emiDelta || 0));
  const emiRose = impact.emiDelta > 0;
  const emiFell = impact.emiDelta < 0;

  const tenureDelta = impact.tenureDeltaYears;
  const tenureRose =
    impact.tenureFeasible && Number.isFinite(tenureDelta) && tenureDelta > 0.05;
  const tenureFell =
    impact.tenureFeasible && Number.isFinite(tenureDelta) && tenureDelta < -0.05;

  return (
    <div className="iri-results" aria-live="polite">
      <CalculatorPrecisionToggle className="calc-precision-toggle--inline" />
      <div className="iri-results__baseline">
        <p className="iri-results__baseline-label">Current EMI at {formatInterestRatePct(impact.baseRate)}</p>
        <p className="iri-results__baseline-value">{baseEmiDisplay}</p>
        <p className="iri-results__baseline-meta">
          After a {formatSignedPct(impact.rateChangePct)} move →{' '}
          {formatInterestRatePct(impact.newRate)}
        </p>
      </div>

      <div className="iri-results__grid">
        <div className="iri-outcome">
          <h4 className="iri-outcome__title">If tenure is unchanged</h4>
          <p className="iri-outcome__eyebrow">New EMI</p>
          <p className="iri-outcome__value">{newEmiDisplay}</p>
          <p className="iri-outcome__delta">
            {emiRose
              ? `EMI rises by about ${emiDeltaDisplay} / month`
              : emiFell
                ? `EMI falls by about ${emiDeltaDisplay} / month`
                : 'EMI stays about the same'}
          </p>
        </div>

        <div
          className={`iri-outcome${
            !impact.tenureFeasible ? ' iri-outcome--warning' : ''
          }`}
        >
          <h4 className="iri-outcome__title">If EMI is unchanged</h4>
          {impact.tenureFeasible ? (
            <>
              <p className="iri-outcome__eyebrow">New tenure</p>
              <p className="iri-outcome__value">
                {formatTenureYearsPrecise(impact.tenureIfEmiFixedYears)}
              </p>
              <p className="iri-outcome__delta">
                {tenureRose
                  ? `About ${formatTenureYearsPrecise(Math.abs(tenureDelta))} longer than ${formatYearsCount(tenureYears)}`
                  : tenureFell
                    ? `About ${formatTenureYearsPrecise(Math.abs(tenureDelta))} shorter than ${formatYearsCount(tenureYears)}`
                    : 'Tenure stays about the same'}
              </p>
            </>
          ) : (
            <>
              <p className="iri-outcome__eyebrow">Tenure extension not enough</p>
              <p className="iri-outcome__warning">
                By increasing the tenure alone, you will not be able to repay the loan unless
                you increase your EMI.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
